import { Box, Button, Flex, Img } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import { MediaImage, ImageDerivative } from '../../generated/types';
import { trackEvent } from '../../utils/analytics';
import { pluralize } from '../../utils/helper';
import Slider from 'react-slick';
import useDragDetection from '../../hooks/useDragDetection';
import CopyrightImage from './CopyrightImage';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'react-image-lightbox/style.css';

export type SliderImage = MediaImage & {
  big: ImageDerivative;
  small: ImageDerivative;
  medium: ImageDerivative;
  tiny: ImageDerivative;
  full: ImageDerivative;
};

interface PageImagesProps {
  images: SliderImage[];
}

export default function PageImages({ images }: PageImagesProps) {
  //getImageStyle(hotel, 'teaser_image', 'hotel_detail_big_thumb')
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isModalOpen) {
      trackEvent('HotelDetail', 'Open', 'Photo Lightbox');
    }
  }, [isModalOpen]);

  // Filter null images
  images = images.filter((i) => i);

  if (images.length === 0) {
    return null;
  }

  const { handleMouseDown, dragging } = useDragDetection();

  const handleChildClick = (i: number) => (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (dragging) {
      e.preventDefault();
      return;
    }

    setIndex(i);
    setIsModalOpen(true);
  };

  let imageCaption: string[] = [];

  if (
    images[index].useNameAsCaption &&
    images[index].name &&
    images[index].name !== ''
  ) {
    imageCaption.push(images[index].name!);
  }

  if (images[index].copyright) {
    imageCaption.push(`© ${images[index].copyright}`);
  }

  return (
    <>
      {isModalOpen && (
        <Lightbox
          {...{
            'data-track-content': '',
            'data-content-piece': 'Hotel Bild Galerie',
            'data-content-name': 'Hotel Detail',
          }}
          imageCaption={imageCaption.join('  -  ')}
          mainSrc={images[index].full.url}
          nextSrc={images[(index + 1) % images.length].full.url}
          prevSrc={images[(index + images.length - 1) % images.length].full.url}
          onCloseRequest={() => setIsModalOpen(false)}
          onMovePrevRequest={() =>
            setIndex((index + images.length - 1) % images.length)
          }
          onMoveNextRequest={() => setIndex((index + 1) % images.length)}
        />
      )}
      <Flex>
        <Box flex="2" pos="relative">
          <CopyrightImage
            alt={images[0].imageAlt || ''}
            title={images[0].imageTitle || ''}
            copyright={images[0].copyright || ''}
            cursor="pointer"
            width="100%"
            src={images.length === 1 ? images[0].big.url : images[0].medium.url}
            onClick={() => {
              setIndex(0);
              setIsModalOpen(true);
            }}
          />

          {images.length > 1 && (
            <Box pos="absolute" bottom="0" left="0" p="4">
              <Button
                size="sm"
                onClick={() => {
                  setIndex(0);
                  setIsModalOpen(true);
                }}
              >
                Fotos ansehen ({images.length}{' '}
                {pluralize(images.length, 'Foto', 'Fotos')})
              </Button>
            </Box>
          )}
        </Box>
        {images.length > 2 && (
          <Box flex="1" ml="1px">
            <Box pos="relative">
              <CopyrightImage
                alt={images[1].imageAlt || ''}
                title={images[1].imageTitle || ''}
                copyright={images[1].copyright || ''}
                cursor="pointer"
                src={images[1].small.url}
                mb="1px"
                onClick={() => {
                  setIndex(1);
                  setIsModalOpen(true);
                }}
                width="100%"
              />
            </Box>

            <Box pos="relative">
              <CopyrightImage
                alt={images[2].imageAlt || ''}
                title={images[2].imageTitle || ''}
                copyright={images[2].copyright || ''}
                cursor="pointer"
                src={images[2].small.url}
                onClick={() => {
                  setIndex(2);
                  setIsModalOpen(true);
                }}
                width="100%"
              />
            </Box>
          </Box>
        )}
      </Flex>
      {images.length > 1 && (
        <Box marginTop={1}>
          <Slider
            variableWidth
            swipeToSlide={true}
            infinite={false}
            arrows={false}
            dots={false}
          >
            {images.map((image, i) => (
              <Box
                key={i}
                onMouseDownCapture={handleMouseDown}
                onClickCapture={handleChildClick(i)}
                cursor="pointer"
                marginRight={1}
                _focus={{
                  boxShadow: 'none',
                  outline: 'none',
                }}
              >
                <Img
                  src={image.tiny?.url || ''}
                  alt={image.imageAlt || ''}
                  title={image.imageTitle || ''}
                  width={100}
                  height={100}
                />
              </Box>
            ))}
          </Slider>
        </Box>
      )}
    </>
  );
}
